import { Box, TextField, Typography } from '@material-ui/core';
import clsx from 'clsx';
import moment from 'moment';
import momentUtils from '@date-io/moment';
import {
  ICalenderBooking, IDateBooking, IDateCalender, IStoreSettingDetail,
} from 'pages/reservationBookingOnlineV2/pages/StepDateAndTimeVisitSelection/types';
import React, {
  memo, useCallback, useEffect, useRef, useState,
} from 'react';
import { Link } from 'react-router-dom';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { StatusBooking } from 'pages/reservationBookingOnlineV2/context';
import { POSSIBLE_MONTH_CODE } from 'constants/setting';
import useStyles from './style';

export enum TypeFilterCalender {
  ALL = 'ALL',
  AFTERNOON = 'AFTERNOON',
  EVENING = 'EVENING',
}
/**
 * 予約可能な最終日を返す（当日基準・C3_CI_ZLABO-2912）
 * '0' = 1か月先まで / '1' = 2か月先まで / '2'・未設定・不明な値 = 制限なし(null)
 */
export const getMaxBookingDate = (possibleMonthCode?: string): string | null => {
  if (possibleMonthCode === POSSIBLE_MONTH_CODE.ONE_MONTH_AHEAD) {
    return moment().add(1, 'months').format('YYYY/MM/DD');
  }
  if (possibleMonthCode === POSSIBLE_MONTH_CODE.TWO_MONTHS_AHEAD) {
    return moment().add(2, 'months').format('YYYY/MM/DD');
  }
  return null;
};

interface ITableCalender {
  storeSetting?: IStoreSettingDetail, // BEFORE_THREE_HOURS | NEXT_DAY | TWO_DAYS_AFTER
  storeName?: string,
  timesCalender: ICalenderBooking[][],
  datesCalender: IDateCalender[],
  dateBooking: IDateBooking,
  handleChangePrevWeek?: () => void,
  handleChangeNextWeek?: () => void,
  handleChangeSelectTime?: (item:ICalenderBooking) => void,
  selectDateDefault?: string,
  handleChangeSelectDate?: (date:string) => void,
}


const TableCalender: React.FC<ITableCalender> = ({
  storeSetting, storeName, timesCalender = [], datesCalender = [], dateBooking, handleChangePrevWeek, handleChangeNextWeek, handleChangeSelectTime, handleChangeSelectDate,
}) => {
  const classes = useStyles();
  const TODAY = moment().format('YYYY/MM/DD');
  const FROM_DATE = moment(datesCalender[0]?.date || '').format('YYYY/MM/DD');
  const TO_DATE = moment(datesCalender[datesCalender?.length - 1]?.date || '').format('YYYY/MM/DD');
  // 予約可能期間は当日基準（0: 1か月先まで / 1: 2か月先まで / それ以外・未設定: 制限なし）
  const MAX_DATE = getMaxBookingDate(storeSetting?.possibleMonthCode);
  const [timesCalenderProp, setTimesCalenderProp] = useState<ICalenderBooking[][]>([]);
  const [showCalender, setShowCalender] = useState<boolean>(true);
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false);
  const [disablePrev, setDisablePrev] = useState<boolean>(true);
  const [disableNext, setDisableNext] = useState<boolean>(false);
  const [selectTime, setSelectTime] = useState<ICalenderBooking | null>(null);
  const tableRef = useRef<any>(0);
  const tableHeaderRef = useRef<any>(0);
  const afternoonRef = useRef<any>(0);
  const eveningRef = useRef<any>(0);

  const getRefByTime = useCallback((time:string) => {
    switch (time) {
      case '12:00':
        return afternoonRef;
      case '16:00':
        return eveningRef;
      default:
        return null;
    }
  }, []);

  const handleSelectTimeCalender = useCallback((item:ICalenderBooking) => {
    if (item?.status !== StatusBooking.AVAILABLE_BOOKING) return;
    setSelectTime(item);
    handleChangeSelectTime && handleChangeSelectTime(item);
  }, [handleChangeSelectTime]);

  const handleSelectDateCalender = useCallback((date:any) => {
    handleChangeSelectDate && handleChangeSelectDate(date); // when use select day in calender of button カレンダーから日付を選択, value  will is column 4th in calender
  }, [handleChangeSelectDate]);

  const handleChangeShowCalender = useCallback(() => {
    setShowCalender((prevState) => !prevState);
  }, []);

  const handleOpenDatePicker = useCallback(() => {
    setDatePickerOpen(true);
  }, []);

  const handleChangePrevWeekProp = useCallback((e) => {
    e.preventDefault();
    (!disablePrev) && handleChangePrevWeek && handleChangePrevWeek();
  }, [disablePrev, handleChangePrevWeek]);

  const handleChangeNextWeekProp = useCallback((e) => {
    e.preventDefault();
    (!disableNext) && handleChangeNextWeek && handleChangeNextWeek();
  }, [disableNext, handleChangeNextWeek]);

  useEffect(() => {
    setTimesCalenderProp(timesCalender);
  }, [timesCalender]);

  useEffect(() => {
    if (!(datesCalender?.length) || !storeSetting) return;
    setDisablePrev(moment(TODAY).isBetween(FROM_DATE, TO_DATE) || moment(TODAY).isSame(FROM_DATE) || moment(TODAY).isSame(TO_DATE));
    // 制限なし（MAX_DATE が null）のときは常に翌週へ進める
    setDisableNext(!!MAX_DATE && (moment(MAX_DATE).isBetween(FROM_DATE, TO_DATE) || moment(MAX_DATE).isSameOrBefore(FROM_DATE) || moment(MAX_DATE).isSameOrBefore(TO_DATE)));
    setSelectTime(null);
  }, [FROM_DATE, MAX_DATE, TODAY, TO_DATE, datesCalender, storeSetting]);

  return (
    <>
      <div className={classes.importantRequest}>
        <Typography variant="h5" className={classes.storeTitle}>
          {storeName || ''}
        </Typography>
        <div className={classes.importantRequest__buttonContainer}>
          <button onClick={handleOpenDatePicker} type="button" className={classes.buttonCalendar}>
            カレンダーから日付を選択
          </button>
          <MuiPickersUtilsProvider utils={momentUtils}>
            <KeyboardDatePicker
              className={classes.buttonDatePicker}
              open={datePickerOpen}
              onClose={() => setDatePickerOpen(false)}
              disableToolbar={true}
              autoOk={true}
              variant="dialog"
              format="YYYY/MM/DD"
              margin="normal"
              id="date-picker-inline-calender"
              label=""
              onChange={handleSelectDateCalender}
              value={moment(dateBooking?.startDate || '').add(3, 'days').format('YYYY/MM/DD')}
              minDate={moment().format('YYYY/MM/DD')}
              maxDate={MAX_DATE || undefined}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
            />
          </MuiPickersUtilsProvider>
        </div>
      </div>
      <p className={classes.calendarLegend}>
        <span className={classes.calendarLegend_red}>◎</span>
        ：予約可
        <br />
        <span className={classes.calendarLegend_red}>△</span>
        ：店舗へお問い合わせください
      </p>
      <div className={clsx({ [classes.tableCalendar_hiddenCalender]: !showCalender })}>
        <table className={classes.calendarNavigation}>
          <tbody>
            <tr>
              <td className={classes.calendarNavigation_btnLeft}><Link className={clsx({ [classes.disabledCursor]: disablePrev })} to="#" onClick={(e) => handleChangePrevWeekProp(e)}>＜ 前週</Link></td>
              <td className={classes.calendarNavigation_btnRight}><Link className={clsx({ [classes.disabledCursor]: disableNext })} to="#" onClick={(e) => handleChangeNextWeekProp(e)}>翌週 ＞</Link></td>
            </tr>
          </tbody>
        </table>
        <div className={classes.wrapperTableCalender}>
          <div className={classes.tableCalendar} ref={tableRef}>
            <table>
              <tbody>
                <tr className={classes.tableCalendar_day} ref={tableHeaderRef}>
                  <td>日時</td>
                  {
                        datesCalender?.map((item) => (
                          <td key={String(item?.date)}><span className={clsx({ [classes.tableCalendar_day_sunday]: item?.class === 0 }, { [classes.tableCalendar_day_saturday]: item?.class === 6 })}>{item?.dayOfWeek}</span></td>
                        ))
                        }
                </tr>
                <>
                  {
                        timesCalenderProp?.map((row:ICalenderBooking[]) => (
                          <tr key={row[0]?.time} className={clsx({ [classes.borderTopBold]: row[0].timeShort?.length > 2 })} ref={getRefByTime(row[0].timeShort)}>
                            <td className={clsx({ [classes.tableCalendar_time]: row[0].timeShort?.length > 2, [classes.tableCalendar_timeHalf]: row[0].timeShort?.length === 2 })}>{row[0].timeShort || ''}</td>
                            {
                              row?.map((column:ICalenderBooking) => (
                                <td key={column?.key} className={clsx({ [classes.tableCalendar_active]: column?.key === selectTime?.key }, { [classes.tableCalendar_on]: column?.class === 0 }, { [classes.tableCalendar_off]: column?.class === 1 }, { [classes.tableCalendar_disable]: column?.class === 2 || column?.class === 1 })}>
                                  <Box role="button" onClick={() => handleSelectTimeCalender(column)}>
                                    <span>{column?.icon}</span>
                                  </Box>
                                </td>
                              ))
                              }
                          </tr>
                        ))
                      }
                </>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(TableCalender);
